import { defineTour } from '../../types';

/**
 * T11 — Supplier RFQ response (brief §7).
 *
 * Any signed-in supplier user: the portal serves only the enquiries this
 * supplier was invited to, scoped by the server, and its capability flags are
 * advisory and NOT authority (SupplierUser.php "OPEN PRODUCT DECISION"), so no
 * step is gated on them. The list is `/vendor/rfqs` (GET only); the enquiry
 * steps run on an enquiry the supplier opened. Nothing is saved until the
 * supplier sends, and the guide ends on the send control without pressing it —
 * nor "Start from my current offer", which overwrites the form.
 *
 * A closed enquiry shows a notice instead of the form: its form steps pause
 * with an access-safe explanation.
 *
 * Copy: `guidance.tours.T11.*` — draft, for native-language review.
 */
const RECORD = {
  routeId: 'vendor.rfq',
  explainKey: 'guidance.tours.T11.openEnquiry',
} as const;

export const T11 = defineTour({
  id: 'T11',
  version: 1,
  portal: 'vendor',
  titleKey: 'guidance.tours.T11.title',
  summaryKey: 'guidance.tours.T11.summary',
  eligible: (ctx) => ctx.portal === 'vendor',
  pageRoutes: ['vendor.rfqs', 'vendor.rfq'],
  steps: [
    {
      id: 't11-list',
      anchor: 'vendor.rfqs.list',
      route: 'vendor.rfqs',
      titleKey: 'guidance.tours.T11.list.title',
      bodyKey: 'guidance.tours.T11.list.body',
    },
    {
      id: 't11-header',
      anchor: 'vendor.rfq.header',
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T11.header.title',
      bodyKey: 'guidance.tours.T11.header.body',
    },
    {
      id: 't11-requirement',
      anchor: 'vendor.rfq.requirement',
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T11.requirement.title',
      bodyKey: 'guidance.tours.T11.requirement.body',
    },
    {
      // Closed for responses: the notice that replaces the form.
      id: 't11-offer',
      anchor: ['vendor.rfq.offer', 'vendor.rfq.closed-notice'],
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T11.offer.title',
      bodyKey: 'guidance.tours.T11.offer.body',
    },
    {
      id: 't11-documents',
      anchor: 'vendor.rfq.attachments',
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T11.documents.title',
      bodyKey: 'guidance.tours.T11.documents.body',
    },
    {
      // Before a first quote there is no current offer: the form is explained instead.
      id: 't11-current-offer',
      anchor: ['vendor.rfq.current-offer', 'vendor.rfq.offer'],
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T11.currentOffer.title',
      bodyKey: 'guidance.tours.T11.currentOffer.body',
    },
    {
      // From 640px the "Ready to send?" card; below it the phone's send bar.
      id: 't11-send',
      anchor: ['vendor.rfq.send-card', 'vendor.rfq.send-bar'],
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T11.send.title',
      bodyKey: 'guidance.tours.T11.send.body',
      commitNote: true,
    },
  ],
});
